// Applying a scan (ADR 0015): which of a perfusion scan's scanned values a form can take, the answer each becomes, the
// forms and entries a scan can go into, and which values the entry's live form logic lets in. The web matches scanned
// values itself, in a hand-kept copy of the SP Data app's rules, so a new variable or unit rule changes this file,
// the app's and spdata-api's `perfusion-scan-reader.documents.ts` together.
//
// Ported from the SP Data app's `src/features/perfusion-scan/`:
//   - `scannedValues.ts`: `applyableValues`, `liveField`, and the unit answer from
//     `src/features/entry-form/fields/unit/unitAnswer.ts`;
//   - `applyingScan.ts`: `SCANNABLE_VARIABLES`, `hasScannableVariable`, `fittingForms`, `entryChoices`, `takesNewEntry`;
//   - `scanToApply.ts`: `valuesToApply`;
//   - `overwriteReview.ts`: `overwrites` and `variablesToApply`.
// Their tests come over as `__tests__/scannedValues.test.ts`.
//
// Two things differ from the app, both because the web's unit field saves an answer only with its number (ADR 0009):
// a row printed with no result ("incalc", "--") is refused rather than applied as No value, which the web would save
// as `''`; and a refusal is a code with its parts, so the words go through the locale files.

import { cloneDeep } from 'lodash';
import { answerText } from '@/components/data/entry/items/answerText';
import { computeEffectProps, isCalculatedField, isReadOnlyByEffects } from '@/components/data/entry/effects';
import { EntryModel } from './entry';
import { isAnswered, toWire } from './fieldAnswer';
import { FormFieldType, type FormField, type FormFieldChoice, type FormModel } from './form';
import type { PerfusionScanModel, ScannedValue } from './perfusionScan';

/** Why a scanned value can't go into a field, as a code and the parts its words name. */
export type UnmatchedReason =
  | { code: 'noVariable' }
  | { code: 'notInForm'; variable: string }
  | { code: 'notUnitField'; variable: string }
  | { code: 'readTwice'; variable: string }
  | { code: 'unitNotOffered'; unit: string }
  | { code: 'noUnit' }
  | { code: 'noResult' }
  | { code: 'noValueRead' }
  | { code: 'notANumber'; value: string }
  | { code: 'boundNotOffered'; bound: string }
  | { code: 'hidden'; variable: string }
  | { code: 'calculated'; variable: string }
  | { code: 'disabled'; variable: string };

/** A scanned value the form can't take, and why, for the user to read. */
export interface UnmatchedValue {
  label: string;
  reason: UnmatchedReason;
}

/** A unit field's answer: the number, its unit and any inequality, as the field holds them. */
export interface ScannedAnswer {
  value: number;
  unit?: string;
  inequality?: string;
}

/** What applying a scan to a form puts into the entry, and what it leaves. */
export interface ApplyableValues {
  /** Each unit field's answer by field identifier, as the string the field saves. */
  values: Record<string, string>;
  /** The same answers as their parts, for writing into the field. */
  answers: Record<string, ScannedAnswer>;
  /** The variables those answers came from, in the scan's order. */
  matched: string[];
  unmatched: UnmatchedValue[];
}

/** The keys of `vue-i18n`'s `t` this module reads: a key and its named values. */
export type Translate = (key: string, named?: Record<string, unknown>) => string;

const REASON_KEYS: Record<UnmatchedReason['code'], string> = {
  noVariable: 'scans_reason_no_variable',
  notInForm: 'scans_reason_not_in_form',
  notUnitField: 'scans_reason_not_unit_field',
  readTwice: 'scans_reason_read_twice',
  unitNotOffered: 'scans_reason_unit_not_offered',
  noUnit: 'scans_reason_no_unit',
  noResult: 'scans_reason_no_result',
  noValueRead: 'scans_reason_no_value_read',
  notANumber: 'scans_reason_not_a_number',
  boundNotOffered: 'scans_reason_bound_not_offered',
  hidden: 'scans_reason_hidden',
  calculated: 'scans_reason_calculated',
  disabled: 'scans_reason_disabled'
};

/** "unit mEq/L not offered": the reason in words. */
export const unmatchedReasonText = (reason: UnmatchedReason, t: Translate): string => {
  const { code, ...parts } = reason;
  return t(REASON_KEYS[code], parts);
};

/**
 * What a GEM report prints in a row that has no result. A metra tile left blank isn't here: metra fields offer no No
 * value choice.
 */
const NO_RESULT_MARKERS = new Set(['incalc', 'incalculable', '--', '—']);

/**
 * The inequality choice each printed bound stands for. spdata-api stores `<=` and `>=` already, but a Perfusion
 * Companion scan keeps what that app sent.
 */
const BOUND_CHOICES: Readonly<Record<string, string>> = {
  '<': '<=',
  '<=': '<=',
  '≤': '<=',
  '>': '>=',
  '>=': '>=',
  '≥': '>='
};

/** A decimal as a document prints one: "7.22", "-3", ".5". */
const PRINTED_NUMBER = /^[-+]?(\d+\.?\d*|\.\d+)$/;

type Refusal = { refused: UnmatchedReason };

const isRefusal = (outcome: object): outcome is Refusal => 'refused' in outcome;

const refuse = (reason: UnmatchedReason): Refusal => ({ refused: reason });

const isNoResult = (scanned: ScannedValue) => NO_RESULT_MARKERS.has(scanned.value.trim().toLowerCase());

/** The field's live choice labelled `label`, the way the unit field saves an inequality. Labels equal values. */
const offeredChoice = (field: FormField, label: string): FormFieldChoice | undefined =>
  field.options.choices?.find(choice => !choice.archived && choice.label === label);

/** The form's live field for `variable`. An archived field is still in the form's items, but takes no new answer. */
export const liveField = (form: { items?: readonly FormField[] }, variable: string): FormField | undefined =>
  form.items?.find(item => !item.archivedAt && item.variableName === variable);

/**
 * The form's live unit field for the value's variable. A variable the scan read more than once has no one value to
 * apply, so each reading is refused.
 */
const targetField = (
  variable: string,
  form: { items?: readonly FormField[] },
  repeated: ReadonlySet<string>
): FormField | Refusal => {
  const field = liveField(form, variable);
  if (!field) return refuse({ code: 'notInForm', variable });
  if (field.type !== FormFieldType.UNIT) return refuse({ code: 'notUnitField', variable });
  if (repeated.has(variable)) return refuse({ code: 'readTwice', variable });
  return field;
};

/**
 * The unit to save: the printed one when the field offers it, as the field spells it, and none on a field with no unit
 * list. Case is ignored, since the metra prints `l/min` for the form's `L/min`. A Companion scan's unit may not be
 * text, and is refused like any other unit the field doesn't offer.
 */
const offeredUnit = (scanned: ScannedValue, field: FormField): { unit?: string } | Refusal => {
  const units = field.options.units?.list ?? [];
  if (units.length === 0) return {};
  const printed: unknown = scanned.unit;
  if (typeof printed !== 'string') {
    return printed ? refuse({ code: 'unitNotOffered', unit: `${printed}` }) : refuse({ code: 'noUnit' });
  }
  const text = printed.trim();
  if (!text) return refuse({ code: 'noUnit' });
  const offered = units.find(({ unit }) => unit === text) ??
    units.find(({ unit }) => unit.toLowerCase() === text.toLowerCase());
  return offered ? { unit: offered.unit } : refuse({ code: 'unitNotOffered', unit: printed });
};

/**
 * The inequality to save for the printed bound, none when there is none. A Companion scan's bound may not be text, and
 * is refused like any other bound the field doesn't offer.
 */
const offeredBound = (scanned: ScannedValue, field: FormField): { inequality?: string } | Refusal => {
  const printed: unknown = scanned.inequality;
  if (!printed) return {};
  const choice = typeof printed === 'string' ? BOUND_CHOICES[printed.trim()] : undefined;
  const inequality = choice && offeredChoice(field, choice)?.label;
  return inequality ? { inequality } : refuse({ code: 'boundNotOffered', bound: `${printed}` });
};

/** The unit field's answer for the scanned value. */
const answerFor = (scanned: ScannedValue, field: FormField): ScannedAnswer | Refusal => {
  const unit = offeredUnit(scanned, field);
  if (isRefusal(unit)) return unit;
  // The app saves No value here. The web's unit field saves no answer without a number (ADR 0009), so it refuses it.
  if (isNoResult(scanned)) return refuse({ code: 'noResult' });
  const value = scanned.value.trim();
  if (!value) return refuse({ code: 'noValueRead' });
  if (!PRINTED_NUMBER.test(value)) return refuse({ code: 'notANumber', value: scanned.value });
  const bound = offeredBound(scanned, field);
  if (isRefusal(bound)) return bound;
  // Saved as a number, as the unit field saves a typed one: "7.10" is 7.1.
  return { value: Number(value), ...unit, ...bound };
};

/**
 * A unit answer as the app's `unitAnswer` writes it, which is what the web's unit field saves (`toWire`): a key left
 * undefined is left out.
 */
export const unitAnswer = ({ value, unit, inequality }: ScannedAnswer): string =>
  JSON.stringify({ value, unit, inequality });

/** What the unit field holds for `answer`: the number, the unit, and the field's own inequality choice or none. */
export const unitPatch = (field: FormField, { value, unit, inequality }: ScannedAnswer): Partial<FormField> => ({
  numberValue: value,
  value: unit,
  choiceValue: inequality === undefined ? undefined : offeredChoice(field, inequality)
});

/** The variables more than one scanned value names. */
const repeatedVariables = (values: readonly ScannedValue[]) => {
  const seen = new Set<string>();
  const repeated = new Set<string>();
  for (const { variable } of values) {
    if (!variable) continue;
    if (seen.has(variable)) repeated.add(variable);
    seen.add(variable);
  }
  return repeated;
};

/**
 * The answers a scan gives one form's fields, matched by variable name and keyed by field identifier. A field the scan
 * didn't read gets no key, so applying the values leaves it as it was.
 */
export const applyableValues = (
  scan: Pick<PerfusionScanModel, 'scannedValues'>,
  form: { items?: readonly FormField[] }
): ApplyableValues => {
  const applied: ApplyableValues = { values: {}, answers: {}, matched: [], unmatched: [] };
  const scanned = scan.scannedValues;
  const repeated = repeatedVariables(scanned);
  for (const scannedValue of scanned) {
    const { label, variable } = scannedValue;
    if (!variable) {
      applied.unmatched.push({ label, reason: { code: 'noVariable' } });
      continue;
    }
    const field = targetField(variable, form, repeated);
    if (isRefusal(field)) {
      applied.unmatched.push({ label, reason: field.refused });
      continue;
    }
    const answer = answerFor(scannedValue, field);
    if (isRefusal(answer)) {
      applied.unmatched.push({ label, reason: answer.refused });
      continue;
    }
    applied.answers[field.identifier] = answer;
    applied.values[field.identifier] = unitAnswer(answer);
    applied.matched.push(variable);
  }
  return applied;
};

/**
 * Every variable a perfusion scan can read: spdata-api's `PERFUSION_SCAN_DOCUMENTS`, the metra screen's six and the
 * GEM report's fourteen. Kept in step with that list by hand.
 */
export const SCANNABLE_VARIABLES: ReadonlySet<string> = new Set([
  'NMP_RESERVOIR_TEMP',
  'NMP_HEPATIC_ARTERY_PRESS',
  'NMP_HEPATIC_ARTERY_FLOW',
  'NMP_PORTAL_VEIN_FLOW',
  'NMP_INFERIOR_VENA_CAVA_PRESS',
  'NMP_INFERIOR_VENA_CAVA_FLOW',
  'NMP_PH',
  'NMP_PCO2_MMHG',
  'NMP_PO2_MMHG',
  'NMP_SODIUM',
  'NMP_POTASSIUM',
  'NMP_CHLORIDE',
  'NMP_CALCIUM_MMOL_L',
  'NMP_GLUCOSE_MG_DL',
  'NMP_LACTATE_MMOL_L',
  'NMP_TOTAL_HB_G_DL',
  'NMP_TCO2',
  'NMP_BE',
  'NMP_HCO3',
  'NMP_HEMATOCRIT'
]);

/** The variable of the field that holds when an entry's values were measured. */
export const MEASURED_AT_VARIABLE = 'DATE_TIME_NMP_PERFUSION_MEASURE';

/** The variable of the Time series-NMP timepoint dropdown. */
export const TIMEPOINT_VARIABLE = 'NMP_PERFUSION_TIMEPOINT';

/** Whether the form has a live field a perfusion scan can fill. */
export const hasScannableVariable = (form: { items?: readonly FormField[] }): boolean =>
  form.items?.some(({ archivedAt, variableName }) =>
    !archivedAt && variableName !== undefined && SCANNABLE_VARIABLES.has(variableName)) ?? false;

/** A form a scan's values can go into, and how many of them it takes. */
export interface FittingForm {
  form: FormModel;
  /** How many of the scan's values the form takes. */
  filled: number;
  /** How many of the scan's values matched a variable at all. */
  read: number;
}

/** The `forms` that take at least one of `scan`'s values, in the order given. Each needs its definition (`items`). */
export const fittingForms = (
  scan: Pick<PerfusionScanModel, 'scannedValues'>,
  forms: readonly FormModel[]
): FittingForm[] => {
  const read = scan.scannedValues.filter(({ variable }) => variable).length;
  return forms
    .map(form => ({ form, filled: applyableValues(scan, form).matched.length, read }))
    .filter(({ filled }) => filled > 0);
};

/** An existing entry a scan can be applied to. */
export interface EntryChoice {
  entry: EntryModel;
  /** The entry's timepoint choice, as the form labels it. */
  timepoint?: string;
  /** When the entry's values were measured, as the form shows it. */
  measuredAt?: string;
  /** Whether the entry is at the timepoint suggested for the scan. */
  suggested: boolean;
}

/** The lock and archive a case carries, which leave none of its entries editable. */
export interface CaseState {
  lockedAt?: Date | string | null;
  archivedAt?: Date | string | null;
}

/** `form`'s fields holding what `entry` saved, as the entry would open with them. */
export const fieldsHolding = (form: { items?: readonly FormField[] }, items: EntryModel['items']): FormField[] => {
  const fields = cloneDeep([...(form.items ?? [])]);
  EntryModel.mapWithFormItems(fields, items ?? {});
  return fields;
};

/** What the entry's `variable` field holds, as the entry shows it, if anything. */
const heldAnswerText = (fields: readonly FormField[], variable: string): string | undefined => {
  const field = liveField({ items: fields }, variable);
  if (!field || !isAnswered(field)) return undefined;
  return answerText(field) || undefined;
};

/** Whether an entry can take a scan's values: one a reader can edit, in a case open to edits. */
const isEditable = (entry: EntryModel, form: FormModel, caseState: CaseState | undefined) =>
  !entry.lockedAt && !entry.archivedAt && !caseState?.lockedAt && !caseState?.archivedAt &&
  form.editExistingEntry !== false;

/**
 * The entries of `form` on the case that a scan can be applied to, in the order given: each labelled by its timepoint
 * and measure time, and marked when it is at the `suggestion`. A read-only entry is left out, so a locked or archived
 * case offers none.
 */
export const entryChoices = (
  entries: readonly EntryModel[],
  form: FormModel,
  caseState: CaseState | undefined,
  suggestion: FormFieldChoice | null
): EntryChoice[] => {
  const timepoint = liveField(form, TIMEPOINT_VARIABLE);
  return entries
    .filter(entry => entry.formIdentifier === form.identifier && isEditable(entry, form, caseState))
    .map(entry => {
      const fields = fieldsHolding(form, entry.items);
      const held = timepoint && entry.items?.[timepoint.identifier];
      return {
        entry,
        timepoint: heldAnswerText(fields, TIMEPOINT_VARIABLE),
        measuredAt: heldAnswerText(fields, MEASURED_AT_VARIABLE),
        suggested: suggestion !== null && held !== undefined && `${held}` === `${suggestion.value}`
      };
    });
};

/** Whether the case can take a new entry of `form`: a form that takes one entry is done once the case has it. */
export const takesNewEntry = (form: FormModel, entries: readonly EntryModel[]): boolean =>
  form.enforcements?.onlyOnce !== true || !entries.some(entry => entry.formIdentifier === form.identifier);

/** The effects-pass flags `valuesToApply` reads, as the field would carry them with the scan's values in. */
const refusalOf = (field: FormField, variable: string): UnmatchedReason | undefined => {
  if (field.show === false) return { code: 'hidden', variable };
  if (isCalculatedField(field)) return { code: 'calculated', variable };
  if (field.disable === true || isReadOnlyByEffects(field)) return { code: 'disabled', variable };
  return undefined;
};

/** `fields` with each `answers` written in, and the form's Show, Hide and Disable worked out over them. */
const withAnswersIn = (fields: readonly FormField[], answers: Record<string, ScannedAnswer>): FormField[] => {
  const trial = cloneDeep([...fields]);
  for (const field of trial) {
    const answer = answers[field.identifier];
    if (answer) Object.assign(field, unitPatch(field, answer));
  }
  const changed = new Set(Object.keys(answers));
  for (const field of trial) {
    if (Array.isArray(field.conditions)) {
      computeEffectProps(field, trial, { changed, skipAssign: true });
    }
  }
  return trial;
};

/** What `valuesToApply` puts into the entry. */
export type ValuesToApply = Pick<ApplyableValues, 'values' | 'answers' | 'unmatched'>;

/**
 * What applying `scan` to an entry whose `fields` hold its current answers puts in: the answers of the chosen
 * `variables` only, by field identifier. Every value the form can't take is listed as not matched, and so is each chosen
 * value whose field would be hidden, calculated or disabled once the scan's values are in: the user has to see the
 * value to check it, and be able to change it.
 */
export const valuesToApply = (
  scan: Pick<PerfusionScanModel, 'scannedValues'>,
  fields: readonly FormField[],
  variables: readonly string[]
): ValuesToApply => {
  const applyable = applyableValues(scan, { items: fields });
  const chosen = fields.filter(({ identifier, variableName }) =>
    identifier in applyable.answers && variables.includes(variableName ?? ''));
  const answersOf = (taken: readonly FormField[]) =>
    Object.fromEntries(taken.map(({ identifier }) => [identifier, applyable.answers[identifier]]));
  // Refusing a value can hide another field that showed only because of it, so the states are worked out again until
  // no more are refused.
  const refused = new Map<string, UnmatchedReason>();
  let taken = chosen;
  for (;;) {
    const trial = withAnswersIn(fields, answersOf(taken));
    const kept = taken.filter(({ identifier, variableName = '' }) => {
      const field = trial.find(item => item.identifier === identifier)!;
      const reason = refusalOf(field, variableName);
      if (reason) refused.set(identifier, reason);
      return !reason;
    });
    if (kept.length === taken.length) break;
    taken = kept;
  }
  const unmatched: UnmatchedValue[] = [...applyable.unmatched];
  for (const { identifier, variableName = '', label } of chosen) {
    const reason = refused.get(identifier);
    if (!reason) continue;
    const printed = scan.scannedValues.find(({ variable }) => variable === variableName);
    unmatched.push({ label: printed?.label ?? label, reason });
  }
  const answers = answersOf(taken);
  const values = Object.fromEntries(Object.keys(answers).map(id => [id, applyable.values[id]]));
  return { values, answers, unmatched };
};

/** One field the overwrite review lists, or one an apply fills. */
export interface ReviewRow {
  identifier: string;
  variable: string;
  /** The field's label in the form. */
  label: string;
  /** What the field holds now, as the entry shows it. Empty for a field the apply fills. */
  current: string;
  /** What the scan would put there, as the entry would show it. */
  scanned: string;
}

/** The overwrite review of one entry: the fields a scan would replace a value in, and the empty ones it fills. */
export interface OverwriteReview {
  overwrites: ReviewRow[];
  fills: ReviewRow[];
}

/** The parts of a unit answer, with an absent, `null` or `''` inequality as none, the way spdata-api compares them. */
const answerParts = (wire: string | number | undefined) => {
  if (typeof wire !== 'string') return undefined;
  try {
    const parsed: unknown = JSON.parse(wire);
    if (!parsed || typeof parsed !== 'object') return undefined;
    const { value, unit, inequality } = parsed as Record<string, unknown>;
    return {
      value: value === undefined || value === null || value === '' ? undefined : Number(value),
      unit: unit === undefined || unit === null ? undefined : `${unit}`,
      inequality: inequality === undefined || inequality === null || inequality === '' ? undefined : `${inequality}`
    };
  } catch {
    return undefined;
  }
};

/** Whether two unit answers say the same: the number as a number, the unit as text, and no inequality alike. */
export const sameUnitAnswer = (a: string | number | undefined, b: string | number | undefined): boolean => {
  const left = answerParts(a);
  const right = answerParts(b);
  // A string that doesn't parse, or a value that isn't a number, compares as itself, as spdata-api compares it.
  if (!left || !right || Number.isNaN(left.value) || Number.isNaN(right.value)) return a === b;
  return left.value === right.value && left.unit === right.unit && left.inequality === right.inequality;
};

/** What `field` saves under its own key now. */
export const wireAnswer = (field: FormField): string | number | undefined => toWire(field)[field.identifier];

/**
 * The overwrite review of `scan` against `fields` holding an entry's answers, in the form's order. A field that
 * already holds the scanned value gets no row, and an empty field fills without a choice.
 */
export const overwriteReview = (
  scan: Pick<PerfusionScanModel, 'scannedValues'>,
  fields: readonly FormField[]
): OverwriteReview => {
  const { answers, values } = applyableValues(scan, { items: fields });
  const review: OverwriteReview = { overwrites: [], fills: [] };
  for (const field of fields) {
    const answer = answers[field.identifier];
    if (!answer) continue;
    const scannedField = { ...field, ...unitPatch(field, answer) } as FormField;
    const row: ReviewRow = {
      identifier: field.identifier,
      variable: field.variableName ?? '',
      label: field.label,
      current: isAnswered(field) ? answerText(field) ?? '' : '',
      scanned: answerText(scannedField) ?? ''
    };
    if (!isAnswered(field)) {
      review.fills.push(row);
    } else if (!sameUnitAnswer(wireAnswer(field), values[field.identifier])) {
      review.overwrites.push(row);
    }
  }
  return review;
};

/**
 * The variables whose scanned values go into the entry: every one the review fills or replaces, less those whose
 * fields the user `kept`. A field already holding the scanned value is left out, since writing it changes nothing.
 */
export const variablesToApply = (review: OverwriteReview, kept: ReadonlySet<string>): string[] => [
  ...review.fills.map(({ variable }) => variable),
  ...review.overwrites.filter(({ identifier }) => !kept.has(identifier)).map(({ variable }) => variable)
];
